'use client';

import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import styles from './signIn.module.scss';
import { Button, Checkbox, ButtonSize, ButtonType, Loader, addToast, Input, validIcons, Icon } from '@brahma/components';
import useMutation from '@brahma/hooks/use-mutation';
import { SIGN_IN_URL_POST_CONFIG } from '$modules/onboarding/constants/sign-in';
import { API_URLS } from '$constants/apis';
import { useRouter, useSearchParams } from 'next/navigation';
import { useOnboardingContext } from '$modules/onboarding/context';
import { ContextAction, LogInType } from '$modules/onboarding/types';
import { COMMON, ONBOARDING } from '$locale/constants';
import { GlobalContext } from '$context/global-context';
import { useFetch, useInputsValidation, useLoadingRouter } from '@brahma/hooks';
import type { AddSignInMobileResponse, AddSignInMobilePayload, IBFFResponse } from '@dfg/brahma-types';
import useLocale from '$hooks/use-locale';
import SubHeaderSignIn from './components/subHeader';
import { PAGE_URLS, SEARCH_PARAMS_KEYS } from '$constants';
import CaptchaInput from './components/captcha-input';
import type { CaptchaData } from './signIn.types';
import { interpolateString, validateEmail } from '@brahma/utils';
const SignIn = () => {
  const {
    t
  } = useLocale([ONBOARDING, COMMON]);
  const {
    mutateAsync: addSignIn,
    data: addSignInResponse,
    error: addSignInError
  } = useMutation<IBFFResponse<AddSignInMobileResponse>, AddSignInMobilePayload>({
    ...SIGN_IN_URL_POST_CONFIG,
    component: 'SignIn'
  });
  const {
    routerPush
  } = useLoadingRouter();
  const onboardingContext = useOnboardingContext();
  const router = useRouter();
  const {
    state: layout
  } = useContext(GlobalContext);
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get(SEARCH_PARAMS_KEYS.token);
  const [shouldGenerateCaptcha, setShouldGenerateCaptcha] = useState(true);
  const [captchaInputValue, setCaptchaInputValue] = useState('');
  const [generatedCaptcha, setGeneratedCaptcha] = useState<CaptchaData | null>(null);
  const [checkboxCheck, setCheckboxCheck] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const code = searchParams.get(SEARCH_PARAMS_KEYS.code) || '';
  const {
    values,
    handleChange,
    disableSubmit
  } = useInputsValidation({
    email: ''
  }, {
    email: [validateEmail(t('addEmail.errorMsg'))]
  });
  const handleCaptchaInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCaptchaInputValue(event.target.value);
  };
  const onCaptchaGenerate = (captcha: CaptchaData) => {
    setShouldGenerateCaptcha(false);
    setGeneratedCaptcha(captcha);
  };
  useEffect(() => {
    if (addSignInResponse?.data) {
      const data = addSignInResponse?.data;
      onboardingContext.dispatch({
        type: ContextAction.setAuthenticationConfig,
        payload: {
          email: values.email as string,
          logInType: LogInType.Mobile,
          consentMobile: checkboxCheck,
          maskedEmail: data?.maskedEmail,
          isExistingUser: data?.isExistingUser
        }
      });
      const urlSearchParams = new URLSearchParams(searchParams.toString());
      urlSearchParams.set(SEARCH_PARAMS_KEYS.email, values.email as string);
      router.push(`${PAGE_URLS.onboardingVerificationMobile}?${urlSearchParams}`);
    } else {
      setLoading(false);
    }
  }, [addSignInResponse?.data]);
  useEffect(() => {
    if (addSignInError) {
      if (addSignInError?.error?.dfgMessage) {
        addToast({
          content: addSignInError?.error?.dfgMessage ?? '',
          variant: 'error',
          autoCloseDuration: 5000
        });
      }
      setShouldGenerateCaptcha(true);
      setLoading(false);
    }
  }, [addSignInError]);
  const handleCheckboxChange = () => {
    setCheckboxCheck(!checkboxCheck);
  };
  const handleOtpVerify = async () => {
    setLoading(true);
    addSignIn({
      email: values.email as string,
      captchaId: generatedCaptcha?.id ?? '',
      captchaText: captchaInputValue,
      ...(inviteToken && {
        inviteToken
      })
    });
  };
  const handleNavigateToWebsite = () => {
    if (process.env.NEXT_PUBLIC_PARTNER_PORTAL_URL) {
      routerPush(process.env.NEXT_PUBLIC_PARTNER_PORTAL_URL);
    }
  };
  const {
    fetch: verifyPreAuthCode,
    data: verifyTokenData,
    loading: verifyTokenDataLoading,
    error: verifyTokenError
  } = useFetch<IBFFResponse<any>>({
    url: interpolateString(API_URLS.verifyPreAuthCode, {
      code: code
    }),
    component: 'SignIn'
  }, false);
  useEffect(() => {
    if (code.length > 0) {
      verifyPreAuthCode();
    }
  }, []);
  useEffect(() => {
    if (verifyTokenData && verifyTokenData.data.redirectUrl) {
      const urlSearchParams = new URLSearchParams(searchParams.toString());
      router.push(`${verifyTokenData.data.redirectUrl}&${urlSearchParams}`);
    }
  }, [verifyTokenData]);
  useEffect(() => {
    if (verifyTokenError) {
      const urlSearchParams = new URLSearchParams(searchParams.toString());
      router.push(`${PAGE_URLS.signInError}?${urlSearchParams}`);
    }
  }, [verifyTokenError]);
  const inputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
  };
  return <div className={styles.mainContainer} data-sentry-component="SignIn" data-sentry-source-file="index.tsx">
      <Loader loaderText={t('loader.text')} show={loading || verifyTokenDataLoading} data-sentry-element="Loader" data-sentry-source-file="index.tsx" />
      <div className={styles.container}>
        <div className={styles.bodyContainer}>
          <SubHeaderSignIn data-sentry-element="SubHeaderSignIn" data-sentry-source-file="index.tsx" />
          <div className={styles.contentContainer}>
            <div className={styles.contentUpperContainer}>
              <Input placeholder={t('addEmail.placeholder')} onChange={inputChanged} type="email" label={t('addEmail.title')} value={values.email} name="email" errorMessage={null} prefixIcon={<Icon iconName={validIcons.mail01} height="20" width="20" />} data-testid="inputBox" data-sentry-element="Input" data-sentry-source-file="index.tsx" />

              {/* Captcha input */}
              <CaptchaInput captchaInputValue={captchaInputValue} handleCaptchaInputChange={handleCaptchaInputChange} captchaBase64={generatedCaptcha?.captchaBase64 as string} testId="applicantOnboardingCaptcha" onCaptchaGenerate={onCaptchaGenerate} shouldGenerateCaptcha={shouldGenerateCaptcha} data-sentry-element="CaptchaInput" data-sentry-source-file="index.tsx" />
              <div className={styles.checkboxContainer}>
                <Checkbox label={layout.screen.isMobile ? t('signIn.mobile.checkboxDesc') : t('signIn.web.checkboxDesc')} classes={layout.screen.isMobile && {
                checkboxroot: styles.checkboxroot,
                checkboxInput: styles.checkboxInput
              }} checked={checkboxCheck} onChange={handleCheckboxChange} testId="signInCheckbox" data-sentry-element="Checkbox" data-sentry-source-file="index.tsx" />
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <Button disabled={disableSubmit || !captchaInputValue} text={t('signIn.buttonText')} variant={ButtonType.Primary} size={ButtonSize.Large} onClick={handleOtpVerify} className={styles.buttonBox} testPrefixId="signInButton" data-sentry-element="Button" data-sentry-source-file="index.tsx" />
            </div>
            {!layout.screen.isMobile && <div className={styles.stickyBottomSection}>
                <div className={styles.partnerRedirectionText}>
                  {t('signIn.partnerRedirectionText')}
                </div>
                <div className={styles.websiteButtonContainer}>
                  <Button text={t('signIn.bussinessLogin')} onClick={handleNavigateToWebsite} endIcon={<Icon iconName={validIcons.arrowRightRed} />} variant="tertiary" textStyle={styles.bussinessButton} />
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default SignIn;